import { createFileRoute } from "@tanstack/react-router";
import { motion } from "motion/react";
import { Mail, Phone, Clock, MessageCircle } from "lucide-react";
import { SupportForm } from "@/components/forms/SupportForm";
import { Faq } from "@/components/layouts/Faq";
import { createSEOHead, SEO_DATA } from "@/lib/seo";

export const Route = createFileRoute("/(public)/contact")({
  component: RouteComponent,
  head: () => createSEOHead(SEO_DATA.CONTACT),
});

const QUICK_CONTACTS = [
  {
    icon: Mail,
    label: "Email",
    value: "contact@hometoq.com",
    href: "mailto:contact@hometoq.com",
    hint: "Réponse sous 24h ouvrées",
  },
  {
    icon: Phone,
    label: "Téléphone",
    value: "+33 6 24 91 42 65",
    href: "tel:+33624914265",
    hint: "Lun–Ven · 9h–18h",
  },
];

function RouteComponent() {
  return (
    <div>
      {/* HERO */}
      <section className="bg-cream/40 pt-12 lg:pt-16 pb-12 lg:pb-16">
        <div className="container mx-auto px-4 lg:px-6 max-w-5xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex flex-col items-center gap-3"
          >
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary font-button-extra-small uppercase tracking-widest px-3 py-1.5 rounded-full">
              <MessageCircle size={12} />
              Contact
            </div>
            <h1 className="font-h1 text-dark-blue">
              Une question ? <span className="text-primary">On vous répond.</span>
            </h1>
            <p className="font-p text-muted-foreground max-w-xl">
              Que vous soyez chef ou client, notre équipe est là pour vous
              accompagner. Choisissez le canal qui vous convient le mieux.
            </p>
          </motion.div>
        </div>
      </section>

      {/* QUICK CONTACTS */}
      <section className="container mx-auto px-4 lg:px-6 max-w-5xl -mt-8 relative z-10">
        <div className="grid sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
          {QUICK_CONTACTS.map((c, i) => {
            const Icon = c.icon;
            return (
              <motion.a
                key={c.label}
                href={c.href}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="bg-white rounded-2xl border border-border p-5 hover:border-primary/30 hover:shadow-lg transition-all flex items-start gap-4"
              >
                <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                  <Icon className="w-5 h-5 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-button-extra-small uppercase tracking-wider text-muted-foreground">
                    {c.label}
                  </p>
                  <p className="font-button-small text-dark-blue mt-1 break-words">
                    {c.value}
                  </p>
                  <p className="font-small text-muted-foreground mt-1">
                    {c.hint}
                  </p>
                </div>
              </motion.a>
            );
          })}
        </div>
      </section>

      {/* FORM + SIDEBAR */}
      <section className="container mx-auto px-4 lg:px-6 max-w-5xl py-12 lg:py-16">
        <div className="grid lg:grid-cols-[1.4fr_1fr] gap-8 lg:gap-10 items-start">
          {/* Form */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="bg-white rounded-2xl border border-border p-6 lg:p-8 shadow-sm"
          >
            <header className="mb-6">
              <h2 className="font-h3 text-dark-blue">Envoyez-nous un message</h2>
              <p className="font-small text-muted-foreground mt-1">
                Remplissez ce formulaire, nous vous répondons sous 24h ouvrées.
              </p>
            </header>
            <SupportForm />
          </motion.div>

          {/* Sidebar — promesses */}
          <motion.aside
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="flex flex-col gap-4 lg:sticky lg:top-8"
          >
            {/* Réponse rapide */}
            <div className="bg-dark-blue rounded-2xl p-6 text-white relative overflow-hidden">
              <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-primary/30 blur-3xl pointer-events-none" />
              <div className="relative">
                <div className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center mb-3 backdrop-blur-sm">
                  <Clock className="w-5 h-5" />
                </div>
                <p className="font-h4 mb-2">On vous répond sous 24h</p>
                <p className="font-small text-white/75 leading-relaxed">
                  Notre équipe traite chaque demande avec attention. Pour une
                  urgence, privilégiez le téléphone aux horaires ouvrés.
                </p>
              </div>
            </div>

            {/* FAQ teaser */}
            <div className="bg-white rounded-2xl border border-border p-6">
              <p className="font-button-small text-dark-blue mb-2">
                💡 Avant de nous contacter
              </p>
              <p className="font-small text-muted-foreground mb-3 leading-relaxed">
                La majorité des questions trouvent leur réponse dans notre FAQ
                en bas de page.
              </p>
              <a
                href="#faq"
                className="font-button-small text-primary hover:underline"
              >
                Voir la FAQ →
              </a>
            </div>
          </motion.aside>
        </div>
      </section>

      <div id="faq">
        <Faq />
      </div>
    </div>
  );
}
